/**
 * The route registry: one entry per endpoint, carrying everything both sides
 * of the wire need to agree on.
 *
 * Crucially this includes the PERMISSION each route requires. That was
 * previously written only at the call site in v1.ts, which meant the published
 * contract could not say who is allowed to call what - and a permission is
 * part of an API's contract, not an implementation detail.
 *
 * Paths are stored in OpenAPI form (`{id}`) because that is the published
 * shape. `toFastifyPath` converts for the server, so the spec stays canonical
 * and the framework-specific form is derived rather than duplicated.
 */
import { z } from 'zod';
import * as s from './schemas.js';
import { BackendDescription } from './backend.js';
import { SuitabilityFacts, SuitabilityPreview } from './suitability.js';
import { ProjectPlanBody, ProjectPlanResponse } from './project-plans.js';
export const PERMISSIONS = [
    'org:read', 'org:update', 'org:delete',
    'member:read', 'member:invite', 'member:update_role', 'member:remove',
    'project:read', 'project:create', 'project:update', 'project:delete',
    'data:read', 'data:write', 'deploy', 'logs:read',
    'apikey:read', 'apikey:create', 'apikey:revoke',
    'billing:read', 'billing:manage',
    'usage:read',
    'audit:read',
];
const Id = z.object({ id: z.string().uuid() });
export const routes = {
    health: {
        method: 'GET',
        path: '/health',
        summary: 'Liveness. No dependencies - answers even when the database is down.',
        status: 200,
        response: s.Health,
    },
    me: {
        method: 'GET',
        path: '/v1/me',
        summary: 'The caller, their organization, and the permissions this authorization can exercise.',
        authenticated: true,
        status: 200,
        response: s.Me,
    },
    getOrganization: {
        method: 'GET',
        path: '/v1/organization',
        summary: 'The active organization, as selected by the X-Organization-Id header.',
        permission: 'org:read',
        status: 200,
        response: s.OrganizationResponse,
    },
    listOrganizations: {
        method: 'GET',
        path: '/v1/organizations',
        summary: 'Organizations the caller belongs to, with their role in each. Needs no active organization.',
        authenticated: true,
        status: 200,
        response: s.OrganizationListResponse,
    },
    createOrganization: {
        method: 'POST',
        path: '/v1/organizations',
        summary: 'Create an organization. The creator becomes its owner - explicitly, not by inference.',
        authenticated: true,
        body: s.CreateOrganizationBody,
        status: 201,
        response: s.CreatedOrganizationResponse,
    },
    myInvitations: {
        method: 'GET',
        path: '/v1/me/invitations',
        summary: "Pending invitations addressed to the caller's email, across every organization.",
        authenticated: true,
        status: 200,
        response: s.PendingInvitationListResponse,
    },
    redeemInvitation: {
        method: 'POST',
        path: '/v1/invitations/{id}/redeem',
        summary: 'Accept an invitation addressed to your email. Grants the invited role and joins the organization.',
        authenticated: true,
        params: Id,
        status: 200,
        response: s.RedeemedInvitationResponse,
    },
    // --- access tokens --------------------------------------------------------
    //
    // A person's own credential for the CLI and for MCP clients, which have no
    // browser to complete a Clerk session in. Scoped to the active organization
    // because every permission here is: a token that floated free would have to
    // invent its own answer to "which organization?".
    //
    // 'org:read' rather than `authenticated` on purpose - it is the permission
    // every role has, so it costs nobody anything, and it makes selecting an
    // organization a precondition the framework enforces rather than something
    // each handler remembers to check.
    //
    // All three are sessionOnly: a token must not be able to mint another
    // token. Otherwise a leaked one manufactures replacements faster than
    // anyone can revoke them, and revoking the one you found achieves nothing.
    listOAuthConnections: {
        method: 'GET', path: '/v1/me/connections',
        summary: 'Your authorized app connections in this workspace. No credentials.',
        permission: 'org:read', sessionOnly: true, status: 200, response: s.OAuthConnectionListResponse,
    },
    narrowOAuthConnection: {
        method: 'POST', path: '/v1/me/connections/{id}/project',
        summary: 'Permanently restrict one authorization to an existing project. Cannot widen or retarget.',
        permission: 'org:read', sessionOnly: true, params: Id,
        body: z.object({ projectId: z.string().uuid() }).strict(), status: 204,
    },
    revokeOAuthConnection: {
        method: 'DELETE', path: '/v1/me/connections/{id}',
        summary: 'Disconnect one consent family, including access, refresh and pending exchange.',
        permission: 'org:read', sessionOnly: true, params: Id, status: 204,
    },
    listAccessTokens: {
        method: 'GET',
        path: '/v1/me/tokens',
        summary: "The caller's own access tokens for this organization. Prefixes only.",
        permission: 'org:read',
        sessionOnly: true,
        status: 200,
        response: s.AccessTokenListResponse,
    },
    createAccessToken: {
        method: 'POST',
        path: '/v1/me/tokens',
        summary: 'Issue an access token. The plaintext is in this response and nowhere else, ever.',
        permission: 'org:read',
        sessionOnly: true,
        body: s.CreateAccessTokenBody,
        status: 201,
        response: s.IssuedAccessTokenResponse,
    },
    revokeAccessToken: {
        method: 'DELETE',
        path: '/v1/me/tokens/{id}',
        summary: 'Revoke one of your own tokens. Takes effect on its next use.',
        permission: 'org:read',
        sessionOnly: true,
        params: Id,
        status: 204,
    },
    // --- members --------------------------------------------------------------
    listMembers: {
        method: 'GET',
        path: '/v1/members',
        summary: 'Everyone in the organization, with the role NimblyBase holds for them.',
        permission: 'member:read',
        status: 200,
        response: s.MemberListResponse,
    },
    // --- invitations ----------------------------------------------------------
    listInvitations: {
        method: 'GET',
        path: '/v1/invitations',
        summary: 'Invitations for this organization, in any state.',
        permission: 'member:read',
        status: 200,
        response: s.InvitationListResponse,
    },
    createInvitation: {
        method: 'POST',
        path: '/v1/invitations',
        summary: 'Invite an email address to a role. The token is returned once and never again.',
        permission: 'member:invite',
        body: s.CreateInvitationBody,
        status: 201,
        response: s.IssuedInvitationResponse,
    },
    revokeInvitation: {
        method: 'DELETE',
        path: '/v1/invitations/{id}',
        summary: 'Revoke a pending invitation. Its token stops working immediately.',
        permission: 'member:invite',
        params: Id,
        status: 204,
    },
    // --- projects -------------------------------------------------------------
    assessProject: {
        method: 'POST',
        path: '/v1/project-assessments/preview',
        summary: 'Assess declared application requirements before a project exists. Read-only preview; never creates resources or grants provisioning authority.',
        permission: 'project:read',
        readOnly: true,
        body: SuitabilityFacts,
        status: 200,
        response: SuitabilityPreview,
    },
    createProjectPlan: {
        method: 'POST', path: '/v1/project-plans',
        summary: 'Persist a supported project intent with tenant, actor, policy, evidence fingerprint and expiry. No project or provider resources are created.',
        permission: 'project:create', body: ProjectPlanBody, status: 201, response: ProjectPlanResponse,
    },
    getProjectPlan: {
        method: 'GET', path: '/v1/project-plans/{id}',
        summary: 'Read the current state of a plan owned by this user in this organization.',
        permission: 'project:read', params: Id, status: 200, response: ProjectPlanResponse,
    },
    listProjects: {
        method: 'GET',
        path: '/v1/projects',
        summary: 'Projects in the organization.',
        permission: 'project:read',
        status: 200,
        response: s.ProjectListResponse,
    },
    getProject: {
        method: 'GET',
        path: '/v1/projects/{id}',
        summary: 'One project. A project belonging to another organization is simply not found.',
        permission: 'project:read',
        params: Id,
        status: 200,
        response: s.ProjectResponse,
    },
    createProject: {
        method: 'POST',
        path: '/v1/projects',
        summary: 'Create a project. The slug becomes a public hostname and is globally unique.',
        permission: 'project:create',
        body: s.CreateProjectBody,
        status: 201,
        response: s.ProjectResponse,
    },
    bootstrapProject: {
        method: 'POST',
        path: '/v1/bootstrap',
        summary: 'Choose or create the intended project and return its capability-aware first-run instructions.',
        permission: 'project:create',
        body: s.BootstrapBody,
        status: 200,
        response: s.BootstrapResponse,
    },
    pauseProject: {
        method: 'POST',
        path: '/v1/projects/{id}/pause',
        summary: 'Stop the project answering. Data is retained; storage still costs.',
        permission: 'project:update',
        params: Id,
        status: 200,
        response: s.ProjectStateResponse,
    },
    resumeProject: {
        method: 'POST',
        path: '/v1/projects/{id}/resume',
        summary: 'Start answering again. Refused for a project suspended by the platform.',
        permission: 'project:update',
        params: Id,
        status: 200,
        response: s.ProjectStateResponse,
    },
    deleteProject: {
        method: 'DELETE',
        path: '/v1/projects/{id}',
        summary: 'Archive a project. Soft delete - the row and its audit trail remain.',
        permission: 'project:delete',
        params: Id,
        status: 204,
    },
    // --- api keys -------------------------------------------------------------
    listProjectKeys: {
        method: 'GET',
        path: '/v1/projects/{id}/keys',
        summary: 'Keys for a project. Prefixes only - the keys themselves are hashed at rest.',
        permission: 'apikey:read',
        params: Id,
        status: 200,
        response: s.ApiKeyListResponse,
    },
    createApplicationKey: {
        method: 'POST',
        path: '/v1/projects/{id}/application-keys',
        summary: 'Issue a public anon application key. It obeys row and storage policies; never issues a service key.',
        permission: 'project:update',
        params: Id,
        body: s.CreateApplicationKeyBody,
        status: 201,
        response: s.IssuedApiKeyResponse,
    },
    createProjectKey: {
        method: 'POST',
        path: '/v1/projects/{id}/keys',
        summary: 'Issue a key. The plaintext is in this response and nowhere else, ever.',
        permission: 'apikey:create',
        params: Id,
        body: s.CreateApiKeyBody,
        status: 201,
        response: s.IssuedApiKeyResponse,
    },
    // --- project data ---------------------------------------------------------
    //
    // Reaching INSIDE a project from the control plane. The control plane holds
    // only key hashes, so it cannot present a customer's service key - it mints
    // a 60-second platform token instead, which the project's Worker verifies
    // against its own derived secret.
    //
    // The platform token never leaves the control plane and expires after 60
    // seconds. Callers are therefore authorized for the operation they asked
    // for (`data:read`, `data:write`, `deploy`, or `logs:read`) rather than for
    // the internal credential used to carry it out. Service-key issuance, key
    // listing and revocation remain under apikey:* and closed to OAuth. The
    // separate anon-only application-key route above uses project:update.
    getProjectSchema: {
        method: 'GET',
        path: '/v1/projects/{id}/schema',
        summary: "The project database's tables, columns and indexes, in the format apply accepts.",
        permission: 'data:read',
        params: Id,
        status: 200,
        response: s.PassthroughResponse,
    },
    getProjectMigrations: {
        method: 'GET',
        path: '/v1/projects/{id}/migrations',
        summary: 'Schema changes already applied to this project, most recent first.',
        permission: 'data:read',
        params: Id,
        status: 200,
        response: s.PassthroughResponse,
    },
    planProjectSchema: {
        method: 'POST',
        path: '/v1/projects/{id}/schema/plan',
        summary: 'What applying this schema WOULD do. Changes nothing, and says whether it is destructive.',
        permission: 'data:read',
        params: Id,
        body: s.SchemaBody,
        status: 200,
        response: s.PassthroughResponse,
    },
    applyProjectSchema: {
        method: 'POST',
        path: '/v1/projects/{id}/schema/apply',
        summary: 'Apply a schema. Drops are refused unless allowDestructive is true.',
        permission: 'data:write',
        params: Id,
        body: s.ApplySchemaBody,
        status: 200,
        response: s.PassthroughResponse,
    },
    runProjectSql: {
        method: 'POST',
        path: '/v1/projects/{id}/sql',
        summary: 'Run one SQL statement. Use ? placeholders and pass values in params.',
        permission: 'data:write',
        params: Id,
        body: s.RunSqlBody,
        status: 200,
        response: s.PassthroughResponse,
    },
    listProjectFiles: {
        method: 'GET',
        path: '/v1/projects/{id}/files',
        summary: "Objects in the project's file storage, optionally under a prefix.",
        permission: 'data:read',
        params: Id,
        status: 200,
        response: s.PassthroughResponse,
    },
    listProjectFunctions: {
        method: 'GET',
        path: '/v1/projects/{id}/functions',
        summary: "Edge functions deployed for this project, and whether each is live.",
        permission: 'deploy',
        params: Id,
        status: 200,
        response: s.PassthroughResponse,
    },
    deployProjectFunction: {
        method: 'POST',
        path: '/v1/projects/{id}/functions',
        summary: 'Deploy a function to the edge. Its URL is PUBLIC - no key is required to call it.',
        permission: 'deploy',
        params: Id,
        body: s.DeployFunctionBody,
        status: 200,
        response: s.PassthroughResponse,
    },
    scheduleProjectFunction: {
        method: 'POST',
        path: '/v1/projects/{id}/functions/{name}/schedule',
        summary: 'Run a function on a schedule, in UTC. Send null to stop.',
        permission: 'deploy',
        params: z.object({ id: z.string().uuid(), name: z.string() }),
        body: s.SetFunctionScheduleBody,
        status: 200,
        response: s.PassthroughResponse,
    },
    deleteProjectFunction: {
        method: 'DELETE',
        path: '/v1/projects/{id}/functions/{name}',
        summary: 'Remove a function. Its URL stops answering immediately.',
        permission: 'deploy',
        params: z.object({ id: z.string().uuid(), name: z.string() }),
        status: 200,
        response: s.PassthroughResponse,
    },
    listProjectSecrets: {
        method: 'GET',
        path: '/v1/projects/{id}/secrets',
        summary: 'Names of the environment variables set for this project. Values are never returned.',
        permission: 'deploy',
        params: Id,
        status: 200,
        response: s.PassthroughResponse,
    },
    setProjectSecret: {
        method: 'POST',
        path: '/v1/projects/{id}/secrets',
        summary: "Set an environment variable for this project's functions. Write-only: it cannot be read back.",
        permission: 'deploy',
        params: Id,
        body: s.SetFunctionSecretBody,
        status: 200,
        response: s.PassthroughResponse,
    },
    deleteProjectSecret: {
        method: 'DELETE',
        path: '/v1/projects/{id}/secrets/{name}',
        summary: 'Remove an environment variable, and redeploy the functions that were using it.',
        permission: 'deploy',
        params: z.object({ id: z.string().uuid(), name: z.string() }),
        status: 200,
        response: s.PassthroughResponse,
    },
    projectSite: {
        method: 'GET',
        path: '/v1/projects/{id}/site',
        summary: 'Whether a static site is deployed to this project, and when.',
        permission: 'deploy',
        params: Id,
        status: 200,
        response: s.PassthroughResponse,
    },
    planProjectSite: {
        method: 'POST',
        path: '/v1/projects/{id}/site/plan',
        summary: 'Declare a site and be told which files are not uploaded yet. Publishes nothing.',
        permission: 'deploy',
        params: Id,
        body: s.SiteManifestBody,
        status: 200,
        response: s.PassthroughResponse,
    },
    putProjectSiteBlob: {
        method: 'PUT',
        path: '/v1/projects/{id}/site/blob/{hash}',
        summary: 'Upload one file, named by the sha-256 of its own bytes.',
        permission: 'deploy',
        params: z.object({ id: z.string().uuid(), hash: z.string() }),
        status: 200,
        response: s.PassthroughResponse,
    },
    commitProjectSite: {
        method: 'POST',
        path: '/v1/projects/{id}/site/commit',
        summary: 'Publish. One write - the previous site serves until it lands.',
        permission: 'deploy',
        params: Id,
        body: s.SiteManifestBody,
        status: 200,
        response: s.PassthroughResponse,
    },
    projectSiteReleases: {
        method: 'GET',
        path: '/v1/projects/{id}/site/releases',
        summary: 'Every site published recently, newest first, and which one is live.',
        permission: 'deploy',
        params: Id,
        status: 200,
        response: s.PassthroughResponse,
    },
    rollbackProjectSite: {
        method: 'POST',
        path: '/v1/projects/{id}/site/rollback/{release}',
        summary: 'Put an earlier release back. Moves no files - they were never deleted.',
        permission: 'deploy',
        params: z.object({ id: z.string().uuid(), release: z.string() }),
        status: 200,
        response: s.PassthroughResponse,
    },
    deleteProjectSite: {
        method: 'DELETE',
        path: '/v1/projects/{id}/site',
        summary: 'Stop serving the site. The project API is unaffected.',
        permission: 'deploy',
        params: Id,
        status: 200,
        response: s.PassthroughResponse,
    },
    projectAuth: {
        method: 'GET',
        path: '/v1/projects/{id}/auth',
        summary: 'Whether end users can sign up to this project, and where.',
        permission: 'project:read',
        params: Id,
        status: 200,
        response: s.PassthroughResponse,
    },
    enableProjectAuth: {
        method: 'POST',
        path: '/v1/projects/{id}/auth',
        summary: "Turn on sign-up and sign-in for the customer's own end users.",
        permission: 'project:update',
        params: Id,
        body: s.EnableAuthBody,
        status: 200,
        response: s.PassthroughResponse,
    },
    disableProjectAuth: {
        method: 'DELETE',
        path: '/v1/projects/{id}/auth',
        summary: 'Stop serving auth. Existing users are left untouched in your database.',
        permission: 'project:update',
        params: Id,
        status: 200,
        response: s.PassthroughResponse,
    },
    setProjectAuthProvider: {
        method: 'PUT',
        path: '/v1/projects/{id}/auth/providers/{provider}',
        summary: "Configure Google or GitHub sign-in for this project's own end users.",
        permission: 'project:update',
        params: z.object({ id: z.string().uuid(), provider: z.string() }),
        body: s.AuthProviderBody,
        status: 200,
        response: s.PassthroughResponse,
    },
    deleteProjectAuthProvider: {
        method: 'DELETE',
        path: '/v1/projects/{id}/auth/providers/{provider}',
        summary: 'Stop offering this sign-in method. Accounts that used it are untouched.',
        permission: 'project:update',
        params: z.object({ id: z.string().uuid(), provider: z.string() }),
        status: 200,
        response: s.PassthroughResponse,
    },
    listProjectAuthUsers: {
        method: 'GET',
        path: '/v1/projects/{id}/auth/users',
        summary: 'The people who signed up to this project\'s app. Never returns a password hash.',
        // Reading a customer's user directory is reading their data, not their
        // settings - the same permission their own data endpoints require.
        permission: 'data:read',
        params: Id,
        // The query shape is s.AuthUsersQuery. RouteDef carries no query field -
        // every filtered GET here validates in its handler - so it is exported
        // for the handler to parse rather than declared and ignored.
        status: 200,
        response: s.PassthroughResponse,
    },
    revokeProjectAuthUser: {
        method: 'POST',
        path: '/v1/projects/{id}/auth/users/{userId}/revoke',
        summary: 'Sign one person out of this project\'s app, leaving everybody else alone.',
        // project:update, NOT the project:delete that revoking everybody
        // carries. The blast radius is one account and it is reversible - they
        // sign back in - so this is the support action a developer takes while
        // debugging, and the wide one stays the act you need an owner for.
        permission: 'data:write',
        params: s.AuthUserParams,
        status: 200,
        response: s.PassthroughResponse,
    },
    projectAuthEmailPreview: {
        method: 'GET',
        path: '/v1/projects/{id}/auth/email-preview',
        summary: 'What a user of this app receives when they reset a password or confirm an address.',
        // Reading settings, not user data: the preview contains no user and no
        // live token, so it needs no more than seeing the project does.
        permission: 'project:read',
        params: Id,
        // The query shape is s.EmailPreviewQuery, parsed in the handler - see
        // the note on listProjectAuthUsers for why RouteDef carries no query.
        status: 200,
        response: s.PassthroughResponse,
    },
    setProjectAuthUserDisabled: {
        method: 'POST',
        path: '/v1/projects/{id}/auth/users/{userId}/disabled',
        summary: 'Switch one account off, or back on, without touching anybody else.',
        // The same project:update as signing one person out, and for the same
        // reason: one account, and reversible. It is the stronger of the two -
        // they cannot sign back in until somebody turns it on again - but a
        // support action that can be undone is not the act that needs an owner.
        permission: 'data:write',
        params: s.AuthUserParams,
        body: s.SetAuthUserDisabledBody,
        status: 200,
        response: s.PassthroughResponse,
    },
    revokeProjectAuthSessions: {
        method: 'POST',
        path: '/v1/projects/{id}/auth/revoke',
        summary: 'End every end-user session now, and stop every token already issued.',
        // This mutates end-user data rather than project configuration. It is the
        // wide incident action, while the single-user routes above are narrow.
        permission: 'data:write',
        params: Id,
        status: 200,
        response: s.PassthroughResponse,
    },
    describeProject: {
        method: 'GET',
        path: '/v1/projects/{id}/describe',
        summary: 'Backend configuration, limitations and unavailable reasons. Does not probe live health or grant entitlements.',
        permission: 'project:read',
        params: Id,
        status: 200,
        response: BackendDescription,
    },
    diagnoseProject: {
        method: 'GET',
        path: '/v1/projects/{id}/diagnose',
        summary: 'Why this project is not working, in the order things actually break.',
        permission: 'logs:read',
        params: Id,
        status: 200,
        response: s.PassthroughResponse,
    },
    projectRows: {
        method: 'GET',
        path: '/v1/projects/{id}/rows/{table}',
        summary: 'Read rows through the policy layer, optionally as the role an app would use.',
        permission: 'data:read',
        params: z.object({ id: z.string().uuid(), table: z.string() }),
        status: 200,
        response: s.PassthroughResponse,
    },
    insertProjectRows: {
        method: 'POST',
        path: '/v1/projects/{id}/rows/{table}',
        summary: 'Insert rows through the policy layer.',
        permission: 'data:write',
        params: z.object({ id: z.string().uuid(), table: z.string() }),
        status: 200,
        response: s.PassthroughResponse,
    },
    updateProjectRows: {
        method: 'PATCH',
        path: '/v1/projects/{id}/rows/{table}',
        summary: 'Patch the rows a filter selects. A filter is required.',
        permission: 'data:write',
        params: z.object({ id: z.string().uuid(), table: z.string() }),
        status: 200,
        response: s.PassthroughResponse,
    },
    deleteProjectRows: {
        method: 'DELETE',
        path: '/v1/projects/{id}/rows/{table}',
        summary: 'Remove the rows a filter selects. A filter is required.',
        permission: 'data:write',
        params: z.object({ id: z.string().uuid(), table: z.string() }),
        status: 200,
        response: s.PassthroughResponse,
    },
    listProjectPolicies: {
        method: 'GET',
        path: '/v1/projects/{id}/policies',
        summary: 'Row policies for this project: who may do what to which rows.',
        permission: 'data:read',
        params: Id,
        status: 200,
        response: s.PassthroughResponse,
    },
    putProjectPolicy: {
        method: 'POST',
        path: '/v1/projects/{id}/policies',
        summary: 'Write a policy, opening a table to a role. Replaces one of the same name.',
        permission: 'data:write',
        params: Id,
        body: s.PolicyBody,
        status: 201,
        response: s.PassthroughResponse,
    },
    deleteProjectPolicy: {
        method: 'DELETE',
        path: '/v1/projects/{id}/policies/{policyId}',
        summary: 'Remove a policy. Whatever it allowed stops being allowed immediately.',
        permission: 'data:write',
        params: z.object({ id: z.string().uuid(), policyId: z.string() }),
        status: 200,
        response: s.PassthroughResponse,
    },
    listProjectBuckets: {
        method: 'GET',
        path: '/v1/projects/{id}/buckets',
        summary: 'Logical buckets in this project, and what each one allows.',
        permission: 'data:read',
        params: Id,
        status: 200,
        response: s.PassthroughResponse,
    },
    putProjectBucket: {
        method: 'PUT',
        path: '/v1/projects/{id}/buckets/{bucket}',
        summary: 'Create a bucket, or change who may read and write it.',
        permission: 'data:write',
        params: z.object({ id: z.string().uuid(), bucket: z.string() }),
        body: s.StorageBucketBody,
        status: 200,
        response: s.PassthroughResponse,
    },
    deleteProjectBucket: {
        method: 'DELETE',
        path: '/v1/projects/{id}/buckets/{bucket}',
        summary: 'Stop treating a prefix as a bucket. The files are not deleted.',
        permission: 'data:write',
        params: z.object({ id: z.string().uuid(), bucket: z.string() }),
        status: 200,
        response: s.PassthroughResponse,
    },
    getProjectLogs: {
        method: 'GET',
        path: '/v1/projects/{id}/logs',
        summary: "The project's recent requests: what was called, what it answered, and why it failed.",
        permission: 'logs:read',
        params: Id,
        status: 200,
        response: s.PassthroughResponse,
    },
    projectAiModels: {
        method: 'GET',
        path: '/v1/projects/{id}/ai/models',
        summary: 'Models this project can call through the gateway, and what each is for.',
        permission: 'data:read',
        params: Id,
        status: 200,
        response: s.PassthroughResponse,
    },
    projectAiChat: {
        method: 'POST',
        path: '/v1/projects/{id}/ai/chat',
        // The cost is in the summary because this is the only project route
        // where calling it is itself the charge.
        summary: 'Run a model. Charged per token; see ai/models for what is available.',
        permission: 'data:write',
        params: Id,
        body: s.AiChatBody,
        status: 200,
        response: s.PassthroughResponse,
    },
    projectRealtimeTicket: {
        method: 'POST',
        path: '/v1/projects/{id}/realtime/ticket',
        summary: 'A short-lived ticket to subscribe to one channel, so a browser needs no service key.',
        permission: 'data:read',
        params: Id,
        body: s.RealtimeTicketBody,
        status: 200,
        response: s.PassthroughResponse,
    },
    queryProjectVectors: {
        method: 'POST',
        path: '/v1/projects/{id}/vectors/query',
        summary: 'Search the project\'s vector index by text or by a pre-computed embedding.',
        permission: 'data:read',
        params: Id,
        body: s.VectorQueryBody,
        status: 200,
        response: s.PassthroughResponse,
    },
    projectVectorStatus: {
        method: 'GET',
        path: '/v1/projects/{id}/vectors/status',
        summary: 'Whether the vector index has caught up with recent writes.',
        // Reading the index's own state, not the vectors in it. It is still data
        // visibility, and lets a read-scoped client wait for an index to settle.
        permission: 'data:read',
        params: Id,
        status: 200,
        response: s.PassthroughResponse,
    },
    upsertProjectVectors: {
        method: 'POST',
        path: '/v1/projects/{id}/vectors',
        summary: 'Add or replace vectors. Send text to embed server-side, or values directly.',
        permission: 'data:write',
        params: Id,
        body: s.VectorUpsertBody,
        status: 200,
        response: s.PassthroughResponse,
    },
    listProjectDomains: {
        method: 'GET',
        path: '/v1/projects/{id}/domains',
        summary: "Custom domains on this project, and the DNS records each still needs.",
        permission: 'project:read',
        params: Id,
        status: 200,
        response: s.PassthroughResponse,
    },
    addProjectDomain: {
        method: 'POST',
        path: '/v1/projects/{id}/domains',
        // Said in the summary because it is the thing people do not expect:
        // connecting a domain does not make it work, creating the records does.
        summary: 'Connect a domain you own. Returns the DNS records you must create for it.',
        permission: 'project:update',
        params: Id,
        body: s.AddDomainBody,
        status: 201,
        response: s.PassthroughResponse,
    },
    checkProjectDomain: {
        method: 'POST',
        path: '/v1/projects/{id}/domains/{domainId}/check',
        summary: 'Ask now whether the DNS records have taken effect, rather than waiting for the next check.',
        // A write, even though it reads like a read: it records what the
        // provider said. A read-only token must not be able to drive it.
        permission: 'project:update',
        params: z.object({ id: z.string().uuid(), domainId: z.string().uuid() }),
        status: 200,
        response: s.PassthroughResponse,
    },
    deleteProjectDomain: {
        method: 'DELETE',
        path: '/v1/projects/{id}/domains/{domainId}',
        summary: 'Disconnect a domain. It stops serving immediately; your DNS records are yours to remove.',
        permission: 'project:update',
        params: z.object({ id: z.string().uuid(), domainId: z.string().uuid() }),
        status: 200,
        response: s.PassthroughResponse,
    },
    revokeKey: {
        method: 'DELETE',
        path: '/v1/keys/{keyId}',
        summary: 'Revoke a key. Takes effect at the edge within seconds.',
        permission: 'apikey:revoke',
        params: z.object({ keyId: z.string().uuid() }),
        status: 204,
    },
};
/** `/v1/projects/{id}` -> `/v1/projects/:id` */
export function toFastifyPath(path) {
    return path.replace(/\{(\w+)\}/g, ':$1');
}
/** The path parameter names, in the order they appear. */
export function pathParams(path) {
    return [...path.matchAll(/\{(\w+)\}/g)].map((m) => m[1]);
}
//# sourceMappingURL=routes.js.map